export default defineNuxtPlugin((nuxtApp) => {
  const log = useLogger('plugin:api-client')
  const sbAccessToken = useCookie('sb-access-token')
  // Izados fuera del callback de auth a propósito: `useCookie` fabrica un ref con
  // su watcher y su BroadcastChannel, y sólo los libera en `onScopeDispose`.
  // Creándolos dentro del listener, cada evento dejaría un par de refs vivos para
  // siempre — una fuga en una tablet del POS que vive días abierta.
  const sbRefreshToken = useCookie('sb-refresh-token')

  // El navegador aborta toda petición en vuelo al empezar a descargar la página,
  // y una petición abortada produce el mismo TypeError que un fallo de red. Sin
  // esta bandera, nuestro propio reload() de restauración de sesión llenaba
  // Sentry de "Load failed" y podía renderizar la pantalla de error encima del
  // POS antes de que la recarga alcanzara a reemplazarla.
  let isUnloading = false
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      isUnloading = true
    })
    window.addEventListener('pagehide', () => {
      isUnloading = true
    })
    // pagehide dispara también al entrar a bfcache, no sólo en una descarga real.
    // Si el navegador restaura la página desde ahí, el heap sigue vivo y la bandera
    // se quedaría pegada en true, colgando toda petición posterior. pageshow dispara
    // en cada carga —restaurada o no— así que resetear siempre es correcto y basta.
    window.addEventListener('pageshow', () => {
      isUnloading = false
    })
  }

  let isAppMounted = false
  nuxtApp.hook('app:mounted', () => {
    isAppMounted = true
    log.info('[mount] App mounted, session modal enabled')
  })

  // Único listener de auth de la app. Va en un plugin —y no en un layout— porque
  // el POS usa `store-cart` y nunca monta `layouts/app.vue`: cableado ahí, el
  // espejo de cookies jamás se sincronizaba para un cajero, que es justo quien
  // sufrió el incidente.
  nuxtApp.hook('app:mounted', async () => {
    const supabase = useSupabaseClient()
    supabase.auth.onAuthStateChange((event, session) => {
      log.info(`[auth] ${event}`, session ? `expires_at=${session.expires_at}` : 'no session')

      const next = nextSessionCookies(event, session)
      if (!next) {
        return
      }

      sbAccessToken.value = next.access
      sbRefreshToken.value = next.refresh

      if (event === 'SIGNED_OUT') {
        localStorage.setItem('tokenRefreshDate', '0')
      }
    })
  })

  // Hook into Nuxt's global $fetch instance
  nuxtApp.hook('app:created', () => {
    const globalFetch = globalThis.$fetch
    const originalFetch = globalFetch

    globalThis.$fetch = async (request, options = {}) => {
      const requestUrl = request.toString()
      const isApiRoute = requestUrl.includes('/api/')

      // Auto-add Authorization header if token exists and it's an API call
      if (isApiRoute && sbAccessToken.value) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${sbAccessToken.value}`,
        }
      }

      try {
        return await originalFetch(request, options)
      }
      catch (error: unknown) {
        // La página se está yendo: esta petición no falló, la matamos nosotros.
        // Se devuelve una promesa que nunca se asienta — no hay a quién
        // responderle, y rechazar dispararía la pantalla de error sobre una
        // página que está a punto de desaparecer.
        if (isUnloading) return new Promise<never>(() => {})

        const fetchError = error as {
          response?: { status?: number }
          message?: string
          statusMessage?: string
          data?: { message?: string, statusMessage?: string, redirectTo?: string }
        }
        const status = fetchError.response?.status

        // Extract error message from various possible locations
        const errorMessage = fetchError.data?.message
          || fetchError.data?.statusMessage
          || fetchError.statusMessage
          || fetchError.message
          || ''

        // Only handle auth errors for API endpoints
        if (!isApiRoute) {
          throw error
        }

        const is401 = status === 401
        const is403BusinessError = status === 403 && errorMessage.includes('business ID')
        const is403PausedSubscription = status === 403 && errorMessage.includes('paused')

        // Don't intercept auth errors on auth pages (login, logout, etc.)
        const currentPath = window.location.pathname
        const isAuthPage = currentPath.startsWith('/auth/')
        if (isAuthPage) {
          log.info('🚫 On auth page, skipping session restoration')
          throw error
        }

        // Handle paused subscription - redirect to paused page
        if (is403PausedSubscription) {
          log.warn('⏸️ Business paused, redirecting to paused page')
          await navigateTo('/paused')
          throw error
        }

        // Handle 401 (token expired) or 403 (business mismatch)
        if (is401 || is403BusinessError) {
          const sessionRestoration = useSessionRestoration()

          // 1. For 401, try silent refresh first
          if (is401) {
            log.info('🔄 Attempting silent token refresh for 401...')
            const refreshed = await sessionRestoration.attemptSilentRefresh()

            if (refreshed) {
              // Retry the request with new token
              log.info('✅ Token refreshed, retrying request')
              const newToken = useCookie('sb-access-token')
              const retryOptions = {
                ...options,
                headers: {
                  ...(options.headers as Record<string, string> || {}),
                  Authorization: `Bearer ${newToken.value}`,
                },
              }
              return await originalFetch(request, retryOptions)
            }
          }

          // 2. Silent refresh failed or 403
          log.warn(`🔐 Session error (${status})`)

          // App not mounted yet — redirect to login to prevent WSOD
          if (!isAppMounted) {
            log.warn(`[auth] 401 before mount, redirecting to /auth/login (url=${requestUrl})`)
            isUnloading = true
            window.location.replace('/auth/login')
            throw error
          }

          // If modal is already open, queue request instead of throwing
          if (sessionRestoration.isOpen()) {
            log.info('🔄 Modal already open, queuing request')
            return new Promise((resolve, reject) => {
              sessionRestoration.queueRequest({
                request: requestUrl,
                options: options as Record<string, unknown>,
                resolve: resolve as (value: unknown) => void,
                reject,
              })
            })
          }

          return new Promise((resolve, reject) => {
            // Queue this request for retry
            sessionRestoration.queueRequest({
              request: requestUrl,
              options,
              resolve,
              reject,
            })

            // Show modal and handle result
            sessionRestoration.showModal()
              .then(async (result) => {
                log.info('📋 Modal closed with result:', JSON.stringify(result))

                if (result.success) {
                  // Clear queued requests since URLs may contain stale business IDs
                  sessionRestoration.rejectAllRequests()
                  // Reload page to get fresh data with correct business context
                  log.info('✅ Session restored, reloading page')
                  isUnloading = true
                  window.location.reload()
                }
                else if (result.cancelled) {
                  // User cancelled - redirect to login
                  log.info('🚪 User cancelled, redirecting to login')
                  sessionRestoration.rejectAllRequests()
                  isUnloading = true
                  // Use setTimeout to ensure redirect happens after Promise chain settles
                  setTimeout(() => {
                    window.location.replace('/auth/login')
                  }, 0)
                }
                else {
                  // Modal closed without success - reject all
                  sessionRestoration.rejectAllRequests()
                }
              })
              .catch((modalError) => {
                // Modal failed to open - reject all queued requests
                log.error('❌ Failed to show session restore modal:', modalError)
                sessionRestoration.rejectAllRequests()
                reject(error) // Reject with original error
              })
          })
        }

        throw error
      }
    }

    // Copy over any existing properties
    Object.setPrototypeOf(globalThis.$fetch, originalFetch)
    Object.assign(globalThis.$fetch, originalFetch)
  })
})
